/**
 * Das Gate vor der Shell: hat dieser Nutzer ueberhaupt einen Betrieb?
 *
 * `GET /api/me` ist die einzige geschuetzte Route, die KEINEN Mandanten braucht — sie antwortet
 * einem Nutzer ohne Mitgliedschaft mit 200 und `memberships: []` statt mit 403. Genau daran
 * haengt diese Entscheidung. Ist die Liste leer, erscheint der Beitritts-Bildschirm ANSTELLE der
 * Shell; die tenant-gebundenen Abrufe der Unterseiten laufen dann gar nicht erst los, und die
 * Wand aus 403-Fehlerkarten entsteht nicht.
 *
 * Warum hier und nicht in der AppShell: die Shell ist bereits die Ebene, die tenant-gebundene
 * Listen laedt (`/api/inquiries` in ihrer Topbar). Wer erst dort abbiegt, hat die fehlgeschlagenen
 * Abrufe schon ausgeloest. Das Gate liegt deshalb in `ProtectedLayout` ueber allem — auch ueber
 * dem CopilotProvider, der sonst eine Chat-Sitzung fuer einen Mandanten oeffnen wuerde, den es
 * fuer diesen Nutzer nicht gibt.
 *
 * Der zweite `/api/me`-Abruf der Shell bleibt bestehen (useApi hat bewusst keinen geteilten
 * Cache); es ist eine kleine GET-Antwort, und ein geteilter Zustand waere hier mehr Mechanik als
 * Nutzen.
 *
 * P3-Ergaenzung (docs/LOOP-ADMIN-BEREICH.md § P3 "Lock-Screen im Frontend"): dieselbe `/api/me`-
 * Antwort traegt seit dem vorigen P3-Kriterium ein `status`-Feld je Mitgliedschaft (Migration 375,
 * "aktiv"|"gesperrt"|"archiviert"). Ist die AKTUELL aktive Mitgliedschaft (`currentMembership`,
 * `lib/membership.ts` - dieselbe Subdomain-Auswahl wie AppShell/Settings bei mehreren Betrieben
 * pro Nutzer) nicht 'aktiv', erscheint `BereichNichtVerfuegbarSeite` STATT der Shell - aus
 * demselben Grund wie beim Beitritts-Bildschirm oben: die tenant-gebundenen Abrufe der Unterseiten
 * sollen gar nicht erst starten (serverseitig ohnehin durch `TenantGesperrtError` in `withTenant`
 * abgefangen, aber ein sichtbares 403-Fehlerkarten-Feld waere hier dieselbe Wand wie beim
 * Auffangnetz - und keine neutrale Hinweisseite). `status` fehlt/`undefined` (Alt-Antworten,
 * Mocks) laesst diesen Zweig bewusst unberuehrt - identisches Verhalten wie zuvor.
 */
import type { ReactNode } from "react";
import { useApi } from "../hooks/useApi";
import { ErrorNote } from "./components";
import type { Me } from "./types";
import { BeitrittsBildschirm } from "../pages/beitritt";
import { BereichNichtVerfuegbarSeite } from "../pages/bereich-gesperrt";
import { currentMembership } from "../lib/membership";

export function MitgliedschaftsGate({ children }: { children: ReactNode }) {
  const { data: me, loading, error, reload } = useApi<Me>("/api/me");

  // Solange unklar ist, ob es eine Mitgliedschaft gibt, wird die Shell NICHT gemountet — sonst
  // starten ihre tenant-gebundenen Abrufe, bevor die Frage beantwortet ist.
  if (!me && loading) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background font-body-md text-body-md text-on-surface-variant">
        Einen Moment …
      </div>
    );
  }

  if (!me && error) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background">
        <div className="w-full max-w-lg">
          <ErrorNote message={error} title="Ihr Konto konnte nicht geladen werden" onRetry={reload} />
        </div>
      </div>
    );
  }

  if (me && me.memberships.length === 0) return <BeitrittsBildschirm email={me.email} />;

  const aktiveMitgliedschaft = currentMembership(me);
  if (aktiveMitgliedschaft?.status && aktiveMitgliedschaft.status !== "aktiv") {
    return <BereichNichtVerfuegbarSeite />;
  }

  return <>{children}</>;
}
